import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Container, Button, Typography, Box, Grid, Paper, Divider } from '@mui/material';
import Layout from './Layout';
import { colors } from '../colors';

const surveyImage = process.env.PUBLIC_URL + '/HomePage-1.webp';

const HomePage = () => {
  return (
    <>
      <Layout>
        {/* Blue Banner */}
        <Box
          sx={{
            backgroundColor: colors.primaryBannerBlue,
            color: 'white',
            padding: { xs: '5px 0', sm: '10px 0' }, // Responsive padding
            textAlign: 'center',
            fontSize: { xs: '1rem', sm: '1.2rem' },
            fontWeight: '500',
          }}
        >
          Welcome to our latest innovations! Follow us for more updates.
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            textAlign: 'center',
            padding: { xs: '30px 10px', sm: '50px 0' }, // Responsive padding
            color: 'black',
            fontFamily: "'Merriweather', Georgia, serif",
          }}
        >
          <Typography
            variant="h2"
            gutterBottom
            sx={{
              color: colors.racineRedDark,
              fontSize: { xs: '2rem', sm: '3rem' }, // Responsive font size
            }}
          >
            Welcome to International Research Survey
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: colors.primaryApplicationText,
              fontSize: { xs: '1.5rem', sm: '1.5rem' },
              margin: { xs: '10px 0', sm: '20px 0' },
            }}
          >
            Unlock the power of data-driven decisions with our intuitive survey solutions.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontStyle: 'italic',
              color: colors.primaryApplicationText,
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              margin: { xs: '10px 0', sm: '20px 0' },
            }}
          >
            We provide insights and data-driven research for better decision-making across industries, helping organizations reach their fullest potential through customized surveys and powerful analytics.
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.racineRedDark,
              color: 'white',
              padding: { xs: '8px 16px', sm: '10px 20px' },
              marginTop: '20px',
            }}
          >
            Get Started
          </Button>

          {/* Image Section with Explicit Responsive Control */}
          <Box mt={4} display="flex" justifyContent="center">
            <Box
              component="img"
              src={surveyImage}
              alt="Customer Satisfaction"
              sx={{
                width: { xs: '80%', sm: '80%', md: '70%' }, // Responsive width settings
                maxWidth: '1000px',                           // Max width for large screens
                height: 'auto',                              // Keep aspect ratio
                maxHeight: '500px',                          // Constrain height if necessary
                borderRadius: '16px', // Rounded corners
                border: '4px solid',                 // Border size
                borderColor: colors.racineRedDark,   // Border color
              }}
            />
          </Box>
        </Box>

        {/* Hero Section */}
        <Container maxWidth="lg" sx={{ padding: { xs: '10px', sm: '15px' } }}>

          <Box textAlign="center" mt={8}>
            <Typography variant="h4" gutterBottom sx={{ color: colors.racineRedDark }}>
              Why Choose Us?
            </Typography>

            <Grid container spacing={3} alignItems="center" justifyContent="center">
              {/* Left Side - Image */}
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/whyus.webp`} // Replace with your image path
                  alt="Why Choose Us"
                  sx={{
                    border: `2px solid ${colors.racineRedDark}`,
                    width: { xs: '100%', sm: '80%', md: '90%' },
                    maxWidth: '450px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </Grid>

              {/* Right Side - Content Box with Border */}
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <Box
                  sx={{
                    border: `2px solid ${colors.racineRedDark}`,
                    borderRadius: '16px',
                    padding: { xs: '20px', sm: '30px' },
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: { xs: '100%', sm: '80%', md: '90%' },
                    maxWidth: '450px',
                    textAlign: 'left',
                    minHeight: { xs: 'auto', md: '385px' }
                  }}
                >
                  <Typography variant="h5" sx={{ color: colors.racineRedDark, mb: 4 }}>
                    Why Choose Our Platform
                  </Typography>

                  {/* Feature: User-Friendly Interface */}
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box component="span" sx={{ mr: 2 }}>
                      <i className="fas fa-users" style={{ color: colors.racineRedDark, fontSize: '1.5rem' }}></i>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#333333', fontSize: '1rem' }}>
                      <strong>User-Friendly Interface:</strong> Our platform is designed with ease of use in mind, making survey creation and data analysis a seamless experience.
                    </Typography>
                  </Box>

                  {/* Feature: Secure and Reliable */}
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box component="span" sx={{ mr: 2 }}>
                      <i className="fas fa-shield-alt" style={{ color: colors.racineRedDark, fontSize: '1.5rem' }}></i>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#333333', fontSize: '1rem' }}>
                      <strong>Secure and Reliable:</strong> We prioritize data privacy and security, ensuring your survey data is safely stored and protected.
                    </Typography>
                  </Box>

                  {/* Feature: Comprehensive Support */}
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box component="span" sx={{ mr: 2 }}>
                      <i className="fas fa-headset" style={{ color: colors.racineRedDark, fontSize: '1.5rem' }}></i>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#333333', fontSize: '1rem' }}>
                      <strong>Comprehensive Support:</strong> Our dedicated support team is here to guide you through every step of your survey project.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Decorative Divider */}
            <Box display="flex" alignItems="center" justifyContent="center" mt={6} mb={6}>
              <Divider sx={{ flexGrow: 1, borderColor: colors.racineRedDark }} />
              <Typography sx={{ px: 2, color: colors.racineRedDark, fontWeight: 'bold' }}>
                • • •
              </Typography>
              <Divider sx={{ flexGrow: 1, borderColor: colors.racineRedDark }} />
            </Box>
          </Box>

          <Box textAlign="center" mt={8}>
            <Grid container spacing={3} alignItems="center" justifyContent="center">
              {/* Left Side - Image */}
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/allinone.webp`} // Replace with your image path
                  alt="Why Choose Us"
                  sx={{
                    border: `2px solid ${colors.racineRedDark}`,
                    width: { xs: '100%', sm: '80%', md: '90%' },
                    maxWidth: '450px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </Grid>

              {/* Right Side - Content Box with Border */}
              <Grid item xs={12} md={6} display="flex" justifyContent="center">
                <Box
                  sx={{
                    border: `2px solid ${colors.racineRedDark}`,
                    borderRadius: '16px',
                    padding: { xs: '20px', sm: '30px' },
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    width: { xs: '100%', sm: '80%', md: '90%' },
                    maxWidth: '450px',
                    textAlign: 'left',
                    minHeight: { xs: 'auto', md: '385px' }
                  }}
                >
                  <Typography variant="h5" sx={{ color: colors.racineRedDark, mb: 4 }}>
                  The Complete Solution for Surveys, Forms, and Research Insights
                  </Typography>
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box component="span" sx={{ mr: 2 }}>
                      <i className="fas fa-headset" style={{ color: colors.racineRedDark, fontSize: '1.5rem' }}></i>
                    </Box>
                    <Typography variant="body1" sx={{ color: '#333333', fontSize: '1rem' }}>
                      PanSurvey simplifies the process of gathering data, making it easy to ask the right questions and gain impactful insights.
                       Our all-in-one platform empowers you to understand customer needs, boost employee satisfaction, and make data-driven decisions to lead in your industry.
                    </Typography>
                  </Box>

                </Box>
              </Grid>
            </Grid>

            {/* Decorative Divider */}
            <Box display="flex" alignItems="center" justifyContent="center" mt={6} mb={6}>
              <Divider sx={{ flexGrow: 1, borderColor: colors.racineRedDark }} />
              <Typography sx={{ px: 2, color: colors.racineRedDark, fontWeight: 'bold' }}>
                • • •
              </Typography>
              <Divider sx={{ flexGrow: 1, borderColor: colors.racineRedDark }} />
            </Box>
          </Box>

          {/* Call to Action Button */}
          <motion.div initial={{ x: '100vw' }} animate={{ x: 0 }} transition={{ type: 'spring', stiffness: 120 }}>
            <Box textAlign="center" mt={5} mb={8}>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  marginRight: '10px',
                  backgroundColor: colors.racineRedDark,
                  '&:hover': { backgroundColor: colors.racineRedLight },
                }}
              >
                Login
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Layout>
    </>
  );
};

export default HomePage;
